import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  IconButton,
  Collapse,
  Tooltip,
  Tabs,
  Tab,
  useTheme,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import Chart from "chart.js/auto";
import * as XLSX from "xlsx";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import {
  selectProjects,
  selectSelectedProject,
  selectInitializationStatus,
  setSelectedProject,
} from "../features/projects/projectSlice";
import { fetchProjectsAsync } from "../features/projects/projectApi";
import { ClockifyTimeEntry } from "../features/clockifyTimeEntries/ClockifyTimeEntry";
import { ClockifyTimeEntriesResponse } from "../features/clockifyTimeEntries/ClockifyTimeEntriesResponse";
import { ClockifyTimeEntriesReportMapper } from "../features/clockifyTimeEntries/ClockifyTimeEntriesReportMapper";
import axiosInstance from "../features/axiosInstance/axiosInstance";
import TimeFormatter from "../features/extensions/TimeFormatter";
import { Project } from "../features/projects/Project";

interface MilestoneHours {
  milestoneId: number;
  milestoneName: string;
  totalHours: number;
}

interface MonthlyRow {
  sortKey: string;
  label: string;
  totalHours: number;
  milestones: MilestoneHours[];
  entries: ClockifyTimeEntry[];
}

const CHART_COLORS = [
  "rgba(54, 162, 235, 0.8)",
  "rgba(255, 99, 132, 0.8)",
  "rgba(75, 192, 192, 0.8)",
  "rgba(255, 159, 64, 0.8)",
  "rgba(153, 102, 255, 0.8)",
  "rgba(255, 205, 86, 0.8)",
  "rgba(255, 105, 180, 0.8)",
  "rgba(0, 188, 212, 0.8)",
  "rgba(139, 195, 74, 0.8)",
  "rgba(121, 85, 72, 0.8)",
  "rgba(63, 81, 181, 0.8)",
  "rgba(233, 30, 99, 0.8)",
];

const CHART_BORDERS = CHART_COLORS.map((c) => c.replace("0.8)", "1)"));

const formatDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
};

const formatTime = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
};

const formatDurationHM = (hours: number): string => {
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:00`;
};

const exportMonthToExcel = (
  row: MonthlyRow,
  project: Project | null,
  projectName: string
) => {
  const milestoneLookup = new Map<number, string>();
  if (project) {
    project.milestones.forEach((m) => milestoneLookup.set(m.id, m.name));
  }

  const sorted = [...row.entries].sort(
    (a, b) => new Date(a.dateStart).getTime() - new Date(b.dateStart).getTime()
  );

  const data = sorted.map((entry) => ({
    Description: entry.description,
    Task:
      milestoneLookup.get(entry.milestoneId) ||
      `Milestone #${entry.milestoneId}`,
    User: entry.developerName,
    "Start Date": formatDate(entry.dateStart),
    "Start Time": formatTime(entry.dateStart),
    "End Date": formatDate(entry.dateEnd),
    "End Time": formatTime(entry.dateEnd),
    "Duration (h)": formatDurationHM(entry.timeSpentInHours),
    "Duration (decimal)": Number(entry.timeSpentInHours.toFixed(2)),
  }));

  const headers = [
    "Description",
    "Task",
    "User",
    "Start Date",
    "Start Time",
    "End Date",
    "End Time",
    "Duration (h)",
    "Duration (decimal)",
  ];

  const worksheet = XLSX.utils.json_to_sheet(data, { header: headers });
  worksheet["!cols"] = [
    { wch: 40 },
    { wch: 25 },
    { wch: 18 },
    { wch: 14 },
    { wch: 14 },
    { wch: 14 },
    { wch: 14 },
    { wch: 14 },
    { wch: 18 },
  ];

  // Pivot sheet: sum of hours per developer
  const developerMap = new Map<string, number>();
  sorted.forEach((entry) => {
    const current = developerMap.get(entry.developerName) || 0;
    developerMap.set(entry.developerName, current + entry.timeSpentInHours);
  });
  const pivotData = Array.from(developerMap.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([developer, hours]) => ({
      Developer: developer,
      "Total Hours (decimal)": Number(hours.toFixed(2)),
    }));
  const pivotHeaders = ["Developer", "Total Hours (decimal)"];
  const pivotWorksheet = XLSX.utils.json_to_sheet(pivotData, {
    header: pivotHeaders,
  });
  pivotWorksheet["!cols"] = [{ wch: 25 }, { wch: 22 }];

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Time Entries");
  XLSX.utils.book_append_sheet(workbook, pivotWorksheet, "Pivot");
  XLSX.writeFile(workbook, `${projectName} - ${row.label}.xlsx`);
};

// ─── Charts ────────────────────────────────────────────────────────────────────

interface HoursChartsProps {
  monthlyRows: MonthlyRow[];
  loading: boolean;
}

const HoursCharts = ({ monthlyRows, loading }: HoursChartsProps) => {
  const barCanvasRef = useRef<HTMLCanvasElement>(null);
  const stackedCanvasRef = useRef<HTMLCanvasElement>(null);
  const doughnutCanvasRef = useRef<HTMLCanvasElement>(null);

  const barChartRef = useRef<Chart | null>(null);
  const stackedChartRef = useRef<Chart | null>(null);
  const doughnutChartRef = useRef<Chart | null>(null);

  // chronological order for charts (oldest → newest)
  const chronological = useMemo(
    () => [...monthlyRows].sort((a, b) => a.sortKey.localeCompare(b.sortKey)),
    [monthlyRows]
  );

  const allMilestoneNames = useMemo(() => {
    const nameSet = new Map<number, string>();
    monthlyRows.forEach((row) =>
      row.milestones.forEach((m) => nameSet.set(m.milestoneId, m.milestoneName))
    );
    return Array.from(nameSet.entries());
  }, [monthlyRows]);

  const overallMilestoneHours = useMemo(() => {
    const map = new Map<number, { name: string; hours: number }>();
    monthlyRows.forEach((row) =>
      row.milestones.forEach((m) => {
        const existing = map.get(m.milestoneId) || {
          name: m.milestoneName,
          hours: 0,
        };
        existing.hours += m.totalHours;
        map.set(m.milestoneId, existing);
      })
    );
    return Array.from(map.values()).sort((a, b) => b.hours - a.hours);
  }, [monthlyRows]);

  // Bar chart – monthly totals
  useEffect(() => {
    if (!barCanvasRef.current || loading || chronological.length === 0) return;

    barChartRef.current?.destroy();

    barChartRef.current = new Chart(barCanvasRef.current, {
      type: "bar",
      data: {
        labels: chronological.map((r) => r.label),
        datasets: [
          {
            label: "Hours",
            data: chronological.map((r) => Math.round(r.totalHours * 100) / 100),
            backgroundColor: "rgba(54, 162, 235, 0.7)",
            borderColor: "rgba(54, 162, 235, 1)",
            borderWidth: 1,
            borderRadius: 4,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (ctx) => {
                const v = ctx.parsed.y;
                const h = Math.floor(v);
                const m = Math.round((v - h) * 60);
                return `${h}h ${m}m`;
              },
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            title: { display: true, text: "Hours" },
            grid: { color: "rgba(0,0,0,0.05)" },
          },
          x: { grid: { display: false } },
        },
      },
    });

    return () => {
      barChartRef.current?.destroy();
      barChartRef.current = null;
    };
  }, [chronological, loading]);

  // Stacked bar chart – milestone breakdown per month
  useEffect(() => {
    if (!stackedCanvasRef.current || loading || chronological.length === 0)
      return;

    stackedChartRef.current?.destroy();

    const datasets = allMilestoneNames.map(([milestoneId, name], idx) => ({
      label: name,
      data: chronological.map((row) => {
        const m = row.milestones.find((ms) => ms.milestoneId === milestoneId);
        return m ? Math.round(m.totalHours * 100) / 100 : 0;
      }),
      backgroundColor: CHART_COLORS[idx % CHART_COLORS.length],
      borderColor: CHART_BORDERS[idx % CHART_BORDERS.length],
      borderWidth: 1,
    }));

    stackedChartRef.current = new Chart(stackedCanvasRef.current, {
      type: "bar",
      data: {
        labels: chronological.map((r) => r.label),
        datasets,
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: "bottom",
            labels: { boxWidth: 12, padding: 12, font: { size: 11 } },
          },
          tooltip: {
            callbacks: {
              label: (ctx) => {
                const v = ctx.parsed.y;
                const h = Math.floor(v);
                const m = Math.round((v - h) * 60);
                return `${ctx.dataset.label}: ${h}h ${m}m`;
              },
            },
          },
        },
        scales: {
          x: { stacked: true, grid: { display: false } },
          y: {
            stacked: true,
            beginAtZero: true,
            title: { display: true, text: "Hours" },
            grid: { color: "rgba(0,0,0,0.05)" },
          },
        },
      },
    });

    return () => {
      stackedChartRef.current?.destroy();
      stackedChartRef.current = null;
    };
  }, [chronological, allMilestoneNames, loading]);

  // Doughnut chart – overall milestone distribution
  useEffect(() => {
    if (!doughnutCanvasRef.current || loading || overallMilestoneHours.length === 0)
      return;

    doughnutChartRef.current?.destroy();

    doughnutChartRef.current = new Chart(doughnutCanvasRef.current, {
      type: "doughnut",
      data: {
        labels: overallMilestoneHours.map((m) => m.name),
        datasets: [
          {
            data: overallMilestoneHours.map(
              (m) => Math.round(m.hours * 100) / 100
            ),
            backgroundColor: overallMilestoneHours.map(
              (_, i) => CHART_COLORS[i % CHART_COLORS.length]
            ),
            borderColor: overallMilestoneHours.map(
              (_, i) => CHART_BORDERS[i % CHART_BORDERS.length]
            ),
            borderWidth: 1,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: "right",
            labels: { boxWidth: 12, padding: 10, font: { size: 11 } },
          },
          tooltip: {
            callbacks: {
              label: (ctx) => {
                const v = ctx.parsed;
                const h = Math.floor(v);
                const m = Math.round((v - h) * 60);
                const total = (ctx.dataset.data as number[]).reduce(
                  (s, n) => s + n,
                  0
                );
                const pct = total > 0 ? ((v / total) * 100).toFixed(1) : "0";
                return `${ctx.label}: ${h}h ${m}m (${pct}%)`;
              },
            },
          },
        },
      },
    });

    return () => {
      doughnutChartRef.current?.destroy();
      doughnutChartRef.current = null;
    };
  }, [overallMilestoneHours, loading]);

  if (loading || monthlyRows.length === 0) return null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mb: 4 }}>
      {/* Monthly totals bar chart */}
      <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Monthly Hours
        </Typography>
        <Box sx={{ height: { xs: 250, sm: 300, md: 350 }, position: "relative" }}>
          <canvas ref={barCanvasRef} />
        </Box>
      </Paper>

      {/* Stacked + Doughnut side by side */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "2fr 1fr" },
          gap: 3,
        }}
      >
        <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Hours by Milestone per Month
          </Typography>
          <Box
            sx={{ height: { xs: 280, sm: 320, md: 380 }, position: "relative" }}
          >
            <canvas ref={stackedCanvasRef} />
          </Box>
        </Paper>

        <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Milestone Distribution
          </Typography>
          <Box
            sx={{ height: { xs: 280, sm: 320, md: 380 }, position: "relative" }}
          >
            <canvas ref={doughnutCanvasRef} />
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

// ─── Main page ─────────────────────────────────────────────────────────────────

const HoursDashboardPage = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const projects = useAppSelector(selectProjects);
  const selectedProject = useAppSelector(selectSelectedProject);
  const initializationStatus = useAppSelector(selectInitializationStatus);
  const timeFormatter = new TimeFormatter();

  const [timeEntries, setTimeEntries] = useState<ClockifyTimeEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedMonths, setExpandedMonths] = useState<Set<string>>(new Set());
  const [selectedYear, setSelectedYear] = useState<string | null>(null);

  useEffect(() => {
    if (initializationStatus === "not-initialized") {
      dispatch(fetchProjectsAsync());
    }
  }, [dispatch, initializationStatus]);

  useEffect(() => {
    if (
      projects.length > 0 &&
      (!selectedProject || selectedProject.id === 0)
    ) {
      dispatch(setSelectedProject(projects[0]));
    }
  }, [projects, selectedProject, dispatch]);

  const fetchProjectTimeEntries = useCallback(async (projectId: number) => {
    setLoading(true);
    try {
      const response = await axiosInstance.get<ClockifyTimeEntriesResponse[]>(
        `/clockifytimeentries/project/${projectId}`
      );
      const mapper = new ClockifyTimeEntriesReportMapper();
      const entries = response.data.map(
        mapper.mapClockifyTimeEntriesResponseToClockifyTimeEntry
      );
      setTimeEntries(entries);
    } catch (error) {
      console.error("Error fetching project time entries:", error);
      setTimeEntries([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedProject && selectedProject.id > 0) {
      setExpandedMonths(new Set());
      setSelectedYear(null);
      fetchProjectTimeEntries(selectedProject.id);
    }
  }, [selectedProject, fetchProjectTimeEntries]);

  const toggleMonth = (sortKey: string) => {
    setExpandedMonths((prev) => {
      const next = new Set(prev);
      if (next.has(sortKey)) {
        next.delete(sortKey);
      } else {
        next.add(sortKey);
      }
      return next;
    });
  };

  const monthlyRows: MonthlyRow[] = useMemo(() => {
    const monthMap = new Map<
      string,
      {
        label: string;
        totalHours: number;
        milestoneMap: Map<number, { name: string; hours: number }>;
        entries: ClockifyTimeEntry[];
      }
    >();

    timeEntries.forEach((entry) => {
      const date = new Date(entry.dateStart);
      const sortKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
      const label = date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
      });

      if (!monthMap.has(sortKey)) {
        monthMap.set(sortKey, {
          label,
          totalHours: 0,
          milestoneMap: new Map(),
          entries: [],
        });
      }

      const row = monthMap.get(sortKey)!;
      row.totalHours += entry.timeSpentInHours;
      row.entries.push(entry);

      const milestone = row.milestoneMap.get(entry.milestoneId) || {
        name:
          selectedProject?.milestones.find((m) => m.id === entry.milestoneId)
            ?.name || `Milestone #${entry.milestoneId}`,
        hours: 0,
      };
      milestone.hours += entry.timeSpentInHours;
      row.milestoneMap.set(entry.milestoneId, milestone);
    });

    return Array.from(monthMap.entries())
      .map(([sortKey, data]) => ({
        sortKey,
        label: data.label,
        totalHours: data.totalHours,
        entries: data.entries,
        milestones: Array.from(data.milestoneMap.entries())
          .map(([milestoneId, m]) => ({
            milestoneId,
            milestoneName: m.name,
            totalHours: m.hours,
          }))
          .sort((a, b) => b.totalHours - a.totalHours),
      }))
      .sort((a, b) => b.sortKey.localeCompare(a.sortKey));
  }, [timeEntries, selectedProject]);

  const years = useMemo(() => {
    const yearSet = new Set<string>();
    monthlyRows.forEach((row) => yearSet.add(row.sortKey.substring(0, 4)));
    return Array.from(yearSet).sort((a, b) => b.localeCompare(a));
  }, [monthlyRows]);

  useEffect(() => {
    if (years.length > 0 && (!selectedYear || !years.includes(selectedYear))) {
      setSelectedYear(years[0]);
    }
  }, [years, selectedYear]);

  const filteredRows = useMemo(() => {
    if (!selectedYear || years.length <= 1) return monthlyRows;
    return monthlyRows.filter((row) => row.sortKey.startsWith(selectedYear));
  }, [monthlyRows, selectedYear, years]);

  const grandTotal = useMemo(
    () => filteredRows.reduce((sum, row) => sum + row.totalHours, 0),
    [filteredRows]
  );

  const isInitializing = initializationStatus !== "initialized";

  return (
    <Container
      maxWidth="xl"
      sx={{
        display: "flex",
        flexDirection: "column",
        pt: 4,
        pb: 6,
        px: { xs: 2, sm: 3, md: 4 },
      }}
    >
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 700,
            color: "text.primary",
            letterSpacing: "-0.5px",
            mb: 1,
          }}
        >
          Hours Dashboard
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Monthly hours breakdown
          {selectedProject && selectedProject.id > 0
            ? ` for ${selectedProject.name}`
            : ""}
        </Typography>
      </Box>

      {isInitializing ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {years.length > 1 && (
            <Box sx={{ mb: 3 }}>
              <Tabs
                value={selectedYear || years[0]}
                onChange={(_, value) => {
                  setSelectedYear(value);
                  setExpandedMonths(new Set());
                }}
                variant="standard"
              >
                {years.map((year) => (
                  <Tab key={year} label={year} value={year} />
                ))}
              </Tabs>
            </Box>
          )}

          <HoursCharts monthlyRows={filteredRows} loading={loading} />

          <Paper elevation={3} sx={{ overflow: "hidden" }}>
            <TableContainer>
              <Table aria-label="hours dashboard table">
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        backgroundColor: theme.palette.background.paper,
                        fontWeight: 600,
                        width: "50px",
                      }}
                    />
                    <TableCell
                      sx={{
                        backgroundColor: theme.palette.background.paper,
                        fontWeight: 600,
                      }}
                    >
                      Month
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        backgroundColor: theme.palette.background.paper,
                        fontWeight: 600,
                      }}
                    >
                      Total Hours
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: theme.palette.background.paper,
                        width: "50px",
                      }}
                    />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                        <CircularProgress size={28} />
                      </TableCell>
                    </TableRow>
                  ) : filteredRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          No time entries found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    <>
                      {filteredRows.map((row) => (
                        <MonthRowGroup
                          key={row.sortKey}
                          row={row}
                          isExpanded={expandedMonths.has(row.sortKey)}
                          onToggle={() => toggleMonth(row.sortKey)}
                          onExport={() =>
                            exportMonthToExcel(
                              row,
                              selectedProject,
                              selectedProject?.name || "Project"
                            )
                          }
                          timeFormatter={timeFormatter}
                          theme={theme}
                        />
                      ))}
                      <TableRow
                        sx={{
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "rgba(255, 255, 255, 0.05)"
                              : "rgba(0, 0, 0, 0.04)",
                        }}
                      >
                        <TableCell />
                        <TableCell>
                          <Typography sx={{ fontWeight: 700 }}>
                            Total
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            sx={{ fontWeight: 700, fontFamily: "monospace" }}
                          >
                            {timeFormatter.toHoursAndMinutes(grandTotal)}
                          </Typography>
                        </TableCell>
                        <TableCell />
                      </TableRow>
                    </>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}
    </Container>
  );
};

// ─── Month row ─────────────────────────────────────────────────────────────────

interface MonthRowGroupProps {
  row: MonthlyRow;
  isExpanded: boolean;
  onToggle: () => void;
  onExport: () => void;
  timeFormatter: TimeFormatter;
  theme: any;
}

const MonthRowGroup = ({
  row,
  isExpanded,
  onToggle,
  onExport,
  timeFormatter,
  theme,
}: MonthRowGroupProps) => {
  return (
    <>
      <TableRow hover sx={{ cursor: "pointer" }} onClick={onToggle}>
        <TableCell sx={{ width: "50px", py: 1.5 }}>
          <IconButton size="small">
            {isExpanded ? (
              <KeyboardArrowDownIcon />
            ) : (
              <KeyboardArrowRightIcon />
            )}
          </IconButton>
        </TableCell>
        <TableCell>
          <Typography sx={{ fontWeight: 500 }}>{row.label}</Typography>
        </TableCell>
        <TableCell align="right">
          <Typography sx={{ fontWeight: 500, fontFamily: "monospace" }}>
            {timeFormatter.toHoursAndMinutes(row.totalHours)}
          </Typography>
        </TableCell>
        <TableCell align="right" sx={{ width: "50px" }}>
          <Tooltip title="Export to Excel">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onExport();
              }}
            >
              <FileDownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell
          colSpan={4}
          sx={{ py: 0, borderBottom: isExpanded ? undefined : "none" }}
        >
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <Box sx={{ pl: 6, pr: 2, py: 1 }}>
              <Table size="small" aria-label="milestone breakdown">
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: "text.secondary",
                        fontSize: "0.8rem",
                      }}
                    >
                      Milestone
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontWeight: 600,
                        color: "text.secondary",
                        fontSize: "0.8rem",
                      }}
                    >
                      Hours
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {row.milestones.map((m) => (
                    <TableRow key={m.milestoneId}>
                      <TableCell
                        sx={{
                          borderBottom: "none",
                          py: 0.75,
                          fontSize: "0.85rem",
                        }}
                      >
                        {m.milestoneName}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          borderBottom: "none",
                          py: 0.75,
                          fontFamily: "monospace",
                          fontSize: "0.85rem",
                          fontWeight: 500,
                        }}
                      >
                        {timeFormatter.toHoursAndMinutes(m.totalHours)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export default HoursDashboardPage;
